// The modern build calls Promise.withResolvers, which needs Safari 17.4. The
// legacy build polyfills it, so older school iPads can still read uploads.
import * as pdfjsLib from 'pdfjs-dist/legacy/build/pdf.mjs';
import { PLACEHOLDER_THUMBNAIL } from './thumbnails';
import { filePreviewUrl } from '../../../utils/filePreview';

// postinstall copies the matching legacy worker into /static, which
// parcel-reporter-static-files-copy serves from the site root. pdf.js refuses a
// worker from a different version, so the two must come from the same install.
pdfjsLib.GlobalWorkerOptions.workerSrc = '/pdf.worker.min.mjs';

// Every document goes through here. These are student uploads, and with eval
// enabled a crafted font can run script on this origin, where the session JWT
// sits in localStorage (CVE-2024-4367). pdf.js renders fonts without eval at a
// small speed cost.
const openPdf = (source) =>
  pdfjsLib.getDocument({ ...source, isEvalSupported: false });

const readPdfData = async (file) => {
  const arrayBuffer = await file.arrayBuffer();
  // pdf.js can detach the buffer; pass a copy so retries stay safe.
  return new Uint8Array(arrayBuffer);
};

/**
 * Generate a thumbnail image for a PDF page
 * @param {Object} pdf - The PDF document object from PDF.js
 * @param {number} pageNumber - The page number (1-indexed)
 * @param {number} scale - Scale factor for thumbnail
 * @returns {Promise<string>} - Data URL of the thumbnail image
 */
export const generatePdfPageThumbnail = async (pdf, pageNumber, scale = 1.2) => {
  try {
    const page = await pdf.getPage(pageNumber);
    const viewport = page.getViewport({ scale });

    const canvas = document.createElement('canvas');
    const context = canvas.getContext('2d');
    canvas.width = Math.ceil(viewport.width);
    canvas.height = Math.ceil(viewport.height);

    context.fillStyle = '#ffffff';
    context.fillRect(0, 0, canvas.width, canvas.height);

    await page.render({
      canvasContext: context,
      viewport,
    }).promise;

    return canvas.toDataURL('image/jpeg', 0.85);
  } catch (error) {
    // Thumbnail generation failed
    return PLACEHOLDER_THUMBNAIL;
  }
};

/**
 * Get the number of pages in a PDF file
 * @param {File} file - The PDF file
 * @returns {Promise<number>} - Number of pages in the PDF
 */
export const getPdfPageCount = async (file) => {
  try {
    const data = await readPdfData(file);
    const pdf = await openPdf({ data }).promise;
    return pdf.numPages;
  } catch (error) {
    // PDF reading failed
    return 1;
  }
};

/**
 * Get PDF info including page count and thumbnails for each page
 * @param {File} file - The PDF file
 * @returns {Promise<{pageCount: number, thumbnails: string[]}>}
 */
export const getPdfInfo = async (file) => {
  try {
    const data = await readPdfData(file);
    const pdf = await openPdf({ data }).promise;
    const pageCount = pdf.numPages;

    // Render sequentially to avoid memory spikes on large PDFs.
    const thumbnails = [];
    for (let pageNumber = 1; pageNumber <= pageCount; pageNumber += 1) {
      // eslint-disable-next-line no-await-in-loop
      thumbnails.push(await generatePdfPageThumbnail(pdf, pageNumber));
    }

    return { pageCount, thumbnails };
  } catch (error) {
    // PDF reading failed
    return { pageCount: 1, thumbnails: [PLACEHOLDER_THUMBNAIL] };
  }
};

/**
 * Get page count for any file type
 * @param {File} file - The file
 * @param {string} fileType - The file type ('pdf', 'image', 'word')
 * @returns {Promise<number>} - Number of pages
 */
export const getFilePageCount = async (file, fileType) => {
  if (fileType === 'pdf') {
    return getPdfPageCount(file);
  }
  return 1;
};

/**
 * Open a PDF that is already in storage, by url.
 *
 * Resumed drafts have no File instance — only the url the upload returned — so
 * this is the only way back to their page images. The url is a
 * /files/handwritten/{blob} 302 to a signed GCS url, which means this can fail on
 * CORS where the File-based path never could; callers must treat failure as
 * "no thumbnail", not as an error.
 */
export const loadPdfFromUrl = async (url) =>
  openPdf({ url: filePreviewUrl(url), withCredentials: false }).promise;

export const readConvertedWord = async (url, originalName) => {
  const pdf = await loadPdfFromUrl(url);
  try {
    const thumbnails = [];
    for (let page = 1; page <= pdf.numPages; page += 1) {
      thumbnails.push(await generatePdfPageThumbnail(pdf, page));
    }
    const file = new File([await pdf.getData()], originalName.replace(/\.docx$/i, '.pdf'), { type: 'application/pdf' });
    return { file, pageCount: pdf.numPages, thumbnails };
  } finally {
    await pdf.destroy();
  }
};
